import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { getPlatformPricing } from "../../../../../../app/store/slices/admin-settings/adminSettings"
import { updateEventDetailsById } from "../../../../../../app/store/slices/event/event"
import { errorToast, successToastTopRight } from "../../../../../../utils/toasts/toasts"

export default function usePlatformFeesPayable() {
    const dispatch = useDispatch();
    const { eventData } = useSelector((state) => state.event);
    const platformPricingSettings = useSelector(state => state.adminSettings.platformPricing);

    const [loading, setLoading] = useState(false);
    const [isEditable, setIsEditable] = useState(false);
    const [value, setValue] = useState(eventData?.commission_paid_by_end_user);

    const [price, setPrice] = useState(350)
    const [qty, setQty] = useState(1)
    const [passPlatform, setPassPlatform] = useState(false)
    const [audience, setAudience] = useState("normalUser")

    const res = compute(price, qty, passPlatform, audience)

    function inr(n) {
        if (!isFinite(n)) return "₹0"
        // Show up to 2 decimals for fractional paise cases
        return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(
            Math.round(n * 100) / 100,
        )
    }

    function calcBookingFee(price) {
        if (price <= 0) return 0

        if (price >= (platformPricingSettings["ticket-min-percentage-decider"] || 100) && price <= (platformPricingSettings["ticket-booking-fee-decider"] || 500)) {
            return platformPricingSettings["booking-fee-fixed-rate"] || 10
        }

        return price * ((platformPricingSettings["booking-fee-percentage"] || 2) / 100)
    }

    function calcPlatformFee(price, audience = "normalUser") {
        if (price <= 0) return 0

        // End-user (default) rules
        if (audience === "normalUser") {
            if (price < (platformPricingSettings["commission-fee-decider"] || 100)) {
                return price * ((platformPricingSettings["commission-fee-percentage"] || 3) / 100)
            }

            return price * ((platformPricingSettings["commission-fee-percentage"] || 3) / 100) + (platformPricingSettings["commission-fee-fixed-rate"] || 8)
        }

        // Business rules: 2% under ₹100; 2% + ₹5 for ₹100+
        if (price < (platformPricingSettings["commission-fee-decider"] || 100)) {
            return price * ((platformPricingSettings["booking-fee-percentage"] || 2) / 100)
        }

        return price * ((platformPricingSettings["booking-fee-percentage"] || 2) / 100) + 5
    }

    function compute(price, qty, passPlatformToBuyer, audience) {
        const p = Math.max(0, price)
        const q = Math.max(0, Math.floor(qty || 0))

        const booking = calcBookingFee(p)
        const platform = calcPlatformFee(p, audience)

        const buyerPer = p + booking + (passPlatformToBuyer ? platform : 0)
        const creatorPer = p - (passPlatformToBuyer ? 0 : platform)
        const platformPer = booking + platform

        return {
            bookingFeePer: booking,
            platformFeePer: platform,
            buyerPaysPer: buyerPer,
            creatorReceivesPer: creatorPer,
            platformEarnsPer: platformPer,
            buyerPaysTotal: buyerPer * q,
            creatorReceivesTotal: creatorPer * q,
            platformEarnsTotal: platformPer * q,
        }
    }

    useEffect(() => {
        if (!platformPricingSettings || Object.keys(platformPricingSettings).length === 0) {
            dispatch(getPlatformPricing())
        }
    }, [dispatch, platformPricingSettings])


    const feesRule = [
        {
            title: "Booking Fees (paid by buyers)",
            rules: [
                "No fees on free tickets.",
                `₹${platformPricingSettings["booking-fee-fixed-rate"] || 10} per ticket for ticket prices between ₹${platformPricingSettings["ticket-min-percentage-decider"] || 100} and ₹${platformPricingSettings["ticket-booking-fee-decider"] || 500} (inclusive).`,
                `${platformPricingSettings["booking-fee-percentage"] || 2}% per ticket for ticket prices less than ₹${platformPricingSettings["ticket-min-percentage-decider"] || 100} or greater than ₹${platformPricingSettings["ticket-booking-fee-decider"] || 500}.`,
            ],
        },
        {
            title: "Platform Fees (for creators – Standard rates)",
            rules: [
                "No fees on free tickets.",
                `${platformPricingSettings["commission-fee-percentage"] || 3}% per ticket for ticket prices less than ₹${platformPricingSettings["commission-fee-decider"] || 100}.`,
                `${platformPricingSettings["commission-fee-percentage"] || 3}% + ₹${platformPricingSettings["commission-fee-fixed-rate"] || 8} per ticket for ticket prices ₹${platformPricingSettings["commission-fee-decider"] || 100} or more.`,
                "Creators can choose to pass platform fees on to buyers. If enabled, buyers will pay both booking and platform fees.",
            ],
        }
    ]


    /**
     * @async function: to submit the updated platform fees payable information
     * Dispatches the updateEventDetailsById action with the new value
     * Handles loading state and displays success/error toasts
     * 
     * @returns {void}
     */
    async function handleSubmit() {
        try {
            setLoading(true);

            const payload = {
                commission_paid_by_end_user: value,
            }

            await dispatch(
                updateEventDetailsById({
                    eventId: eventData?.eventId,
                    eventData: payload
                })
            ).unwrap();

            successToastTopRight("Platform fees payable information updated successfully.");

        } catch (error) {
            errorToast("Failed to update platform fees payable information.");
        } finally {
            setLoading(false);
        }
    }


    return {
        handleSubmit,
        feesRule,
        loading,
        isEditable,
        setIsEditable,
        value,
        setValue,
        setPassPlatform,
        price,
        setPrice,
        qty,
        setQty,
        inr,
        res
    }
}